import React from "react";
import { JobEntityType, PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { ChevronRight, ChevronDown, Briefcase } from "lucide-react";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { getClientDisplayName } from "@/lib/utils/client-utils";

// One accent per source patient; the same dot marks its jobs in the result
// preview so the origin of every transferred job stays readable.
const SOURCE_DOT_COLORS = [
	"bg-amber-400",
	"bg-sky-400",
	"bg-violet-400",
	"bg-rose-400",
	"bg-teal-400",
	"bg-orange-400",
];

interface MergeOverviewProps {
	keeperPatient: PatientEntityType;
	sources: PatientEntityType[];
	/** Ids picked via the free search rather than the duplicate suggestions. */
	manualIds: Set<string>;
	jobsByPatient: Record<string, JobEntityType[]>;
	patientName: (patient: PatientEntityType) => string;
	formatBirthDate: (date: string | null) => string;
}

const JobRow: React.FC<{
	job: JobEntityType;
	clientLabel: (clientId: string | null) => string;
	dotColor?: string;
}> = ({ job, clientLabel, dotColor }) => (
	<div className="flex items-center gap-2 text-xs py-1">
		{dotColor ? (
			<span
				className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`}
				aria-hidden
			/>
		) : (
			<span
				className="w-2 h-2 rounded-full shrink-0 border border-gray-300"
				aria-hidden
			/>
		)}
		<span className="font-medium text-gray-800 truncate">
			{job.title || "Auftrag"}
		</span>
		<span className="text-gray-400 truncate">
			{clientLabel(job.client_id)}
		</span>
		<span className="text-gray-400 ml-auto shrink-0">
			{job.incoming_order_date
				? new Date(job.incoming_order_date).toLocaleDateString("de-DE")
				: ""}
		</span>
	</div>
);

export const MergeOverview: React.FC<MergeOverviewProps> = ({
	keeperPatient,
	sources,
	manualIds,
	jobsByPatient,
	patientName,
	formatBirthDate,
}) => {
	const clientsLookup = useCentralStore((state) => state.clientsLookup);
	const clientLabel = (clientId: string | null) =>
		clientId ? getClientDisplayName(clientsLookup[clientId]) : "";

	const dotColor = (index: number) =>
		SOURCE_DOT_COLORS[index % SOURCE_DOT_COLORS.length];

	// Newest first (created_at, matching the RPC's Auftraggeber rule). The
	// keeper's existing jobs and the incoming ones are shown as separate groups;
	// the source dot marks where each incoming job comes from.
	const byNewest = (a: JobEntityType, b: JobEntityType) =>
		(b.created_at ?? "").localeCompare(a.created_at ?? "");
	const sourceDotByPatient = new Map(
		sources.map((p, index) => [p.id, dotColor(index)])
	);
	const keeperJobs = [...(jobsByPatient[keeperPatient.id] ?? [])].sort(
		byNewest
	);
	const incomingJobs = sources
		.flatMap((p) => jobsByPatient[p.id] ?? [])
		.sort(byNewest);

	// The merged patient's Auftraggeber is derived by the RPC from the most
	// recent job across all involved patients; mirror that here for the preview.
	const newestJob = [keeperJobs[0], incomingJobs[0]]
		.filter(Boolean)
		.sort(byNewest)[0];
	const resultClientLabel = newestJob
		? clientLabel(newestJob.client_id)
		: clientLabel(keeperPatient.client_id);

	return (
		<div>
			<div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] md:gap-x-16 gap-y-3">
				{/* Left: status quo — the staying patient and every source as they
				    are now. Right: the future state after the merge. */}
				<div className="space-y-3">
					<p className="text-sm font-medium text-gray-700">
						Aktueller Stand
					</p>

					<div
						className="bg-green-50 border-x border-b border-green-200 rounded-lg animate-in fade-in slide-in-from-left-2"
						data-testid="merge-overview-keeper"
					>
						{/* The header carries the card's top border so it stays
						    visible while stuck below the page bar. */}
						<div className="sticky top-16 z-[5] bg-green-50 rounded-t-lg px-3 pt-3 pb-2 -mx-px border-x border-t border-green-200 border-b border-b-green-100">
							<div className="flex items-center gap-2">
								<span
									className="w-2.5 h-2.5 rounded-full shrink-0 border border-gray-300 bg-white"
									aria-hidden
								/>
								<p className="font-medium text-gray-900 truncate">
									{patientName(keeperPatient)}
								</p>
								<span className="text-xs text-green-700 ml-auto shrink-0">
									bleibt bestehen
								</span>
							</div>
							<div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
								{keeperPatient.code && (
									<span>Code: {keeperPatient.code}</span>
								)}
								{keeperPatient.birth_date && (
									<span>
										Geb.:{" "}
										{formatBirthDate(
											keeperPatient.birth_date
										)}
									</span>
								)}
								{clientLabel(keeperPatient.client_id) && (
									<span>
										{clientLabel(keeperPatient.client_id)}
									</span>
								)}
							</div>
						</div>
						<div className="px-3 pb-3 pt-2">
							<p className="text-xs text-gray-600 mb-0.5 flex items-center gap-1">
								<Briefcase className="w-3 h-3" />
								{keeperJobs.length === 1
									? "1 Auftrag"
									: `${keeperJobs.length} Aufträge`}
							</p>
							{keeperJobs.map((job) => (
								<JobRow
									key={job.id}
									job={job}
									clientLabel={clientLabel}
								/>
							))}
						</div>
					</div>

					{sources.map((patient, index) => {
						const jobs = [
							...(jobsByPatient[patient.id] ?? []),
						].sort(byNewest);
						return (
							<div
								key={patient.id}
								className="bg-red-50 border-x border-b border-red-200 rounded-lg animate-in fade-in slide-in-from-left-2"
								data-testid="merge-overview-source"
							>
								{/* Identity stays pinned below the page header while
								    this card's jobs scroll with the page; it carries
								    the card's top border so the edge stays visible. */}
								<div className="sticky top-16 z-[5] bg-red-50 rounded-t-lg px-3 pt-3 pb-2 -mx-px border-x border-t border-red-200 border-b border-b-red-100">
									<div className="flex items-center gap-2">
										<span
											className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotColor(
												index
											)}`}
											aria-hidden
										/>
										<p className="font-medium text-gray-900 truncate">
											{patientName(patient)}
										</p>
										{manualIds.has(patient.id) ? (
											<span
												className="text-xs ml-auto shrink-0 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800"
												title="Nicht als Duplikat vorgeschlagen, sondern manuell ausgewählt"
											>
												Manuell ausgewählt · wird
												gelöscht
											</span>
										) : (
											<span className="text-xs text-gray-500 ml-auto shrink-0 line-through decoration-red-300">
												wird gelöscht
											</span>
										)}
									</div>
									<div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
										{patient.code && (
											<span>Code: {patient.code}</span>
										)}
										{patient.birth_date && (
											<span>
												Geb.:{" "}
												{formatBirthDate(
													patient.birth_date
												)}
											</span>
										)}
										{clientLabel(patient.client_id) && (
											<span>
												{clientLabel(patient.client_id)}
											</span>
										)}
									</div>
								</div>
								<div className="px-3 pb-3 pt-2">
									<p className="text-xs text-gray-600 mb-0.5 flex items-center gap-1">
										<Briefcase className="w-3 h-3" />
										{jobs.length === 1
											? "1 Auftrag wird übertragen"
											: `${jobs.length} Aufträge werden übertragen`}
									</p>
									{jobs.map((job) => (
										<JobRow
											key={job.id}
											job={job}
											clientLabel={clientLabel}
											dotColor={dotColor(index)}
										/>
									))}
								</div>
							</div>
						);
					})}
				</div>

				<div
					className="md:hidden flex flex-col items-center text-gray-400 py-1"
					aria-hidden
				>
					<ChevronDown className="w-5 h-5" />
				</div>

				{/* Both columns flow with the page scroll; only the identity
				    headers stick, so moved jobs stay beside the result list. */}
				<div className="relative space-y-3">
					<p className="text-sm font-medium text-green-700">
						Ergebnis nach Zusammenführung
					</p>
					<div
						className="hidden md:flex items-center absolute -left-16 w-16 top-24 pl-1 pr-0.5"
						aria-hidden
					>
						<div className="flex-1 border-t-2 border-dashed border-gray-300" />
						<ChevronRight className="w-5 h-5 -ml-1.5 text-gray-400" />
					</div>
					<div
						className="bg-green-50 border-x border-b border-green-300 rounded-lg animate-in fade-in slide-in-from-right-2"
						data-testid="merge-overview-result"
					>
						<div className="sticky top-16 z-[5] bg-green-50 rounded-t-lg px-4 pt-4 pb-3 -mx-px border-x border-t border-green-300 border-b border-b-green-200">
							<p className="font-semibold text-gray-900">
								{patientName(keeperPatient)}
							</p>
							<div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3">
								{keeperPatient.code && (
									<span>Code: {keeperPatient.code}</span>
								)}
								{keeperPatient.birth_date && (
									<span>
										Geb.:{" "}
										{formatBirthDate(
											keeperPatient.birth_date
										)}
									</span>
								)}
							</div>

							<p
								className="text-xs text-gray-600 mt-2"
								data-testid="merge-overview-auftraggeber"
							>
								Auftraggeber (aus dem neuesten Auftrag):{" "}
								<span className="font-medium text-gray-900">
									{resultClientLabel || "Privat"}
								</span>
							</p>

							<p className="text-xs font-medium text-gray-700 mt-2 flex items-center gap-1">
								<Briefcase className="w-3 h-3" />
								Aufträge danach:{" "}
								{keeperJobs.length + incomingJobs.length}
							</p>
						</div>

						<div className="px-4 pb-4 pt-2 space-y-3">
							{keeperJobs.length > 0 && (
								<div>
									<p className="text-xs text-gray-500 mb-0.5">
										Bereits zugeordnet ({keeperJobs.length})
									</p>
									{keeperJobs.map((job) => (
										<JobRow
											key={job.id}
											job={job}
											clientLabel={clientLabel}
										/>
									))}
								</div>
							)}
							{incomingJobs.length > 0 && (
								<div>
									<p className="text-xs text-gray-500 mb-0.5">
										Werden übertragen ({incomingJobs.length}
										)
									</p>
									{incomingJobs.map((job) => (
										<JobRow
											key={job.id}
											job={job}
											clientLabel={clientLabel}
											dotColor={
												job.patient_id
													? sourceDotByPatient.get(
															job.patient_id
														)
													: undefined
											}
										/>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
